/**
 * FI Task Queue Page (Sprint 171 D-002)
 *
 * Admin-portal page rendering the FI task queue: pledges awaiting FI officer
 * or FI ops action. Composes @aaimplatform/ui primitives (FilterBar, DataGrid,
 * ActionBar, Dialog, MetricCard, Badge, Button) -- no ad-hoc components.
 *
 * Data is served by /api/admin/tasks via the useTasks / useTaskCount hooks.
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  AlertCircle,
  CheckIcon,
  CornerUpLeftIcon,
  UserPlusIcon,
} from 'lucide-react';
import {
  DataGrid,
  FilterBar,
  ActionBar,
  Dialog,
  Badge,
  Button,
  MetricCard,
  METRIC_READ_CAPTIONS,
  Skeleton,
  Textarea,
  Input,
  ShortcutHelpOverlay,
} from '@aaimplatform/ui';
import type {
  FilterDefinition,
  ActiveFilter,
  ColumnDef,
  SavedView,
  ActionDefinition,
} from '@aaimplatform/ui';
import {
  useTasks,
  useTaskCount,
  useBulkAcknowledgeTasks,
  useBulkReturnTasks,
  useReassignTask,
} from '../../hooks/useTasks';
import type {
  Task,
  TaskAgeBand,
  PledgeStatus,
  TaskListQueryOptions,
} from '../../api/services/tasks';
import {
  useKeyboardShortcuts,
  type KeyboardShortcutConfig,
} from '../../hooks/useKeyboardShortcuts';
import { formatCount, formatDateTime } from '../../utils/display-format';
import {
  PLEDGE_STATUS_FILTER_OPTIONS,
  PledgeStatusBadge,
} from '../../utils/status-labels';
import { RouterTableLink } from '../../components/RouterTableLink';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SAVED_VIEWS_STORAGE_KEY = 'aaim-171-task-queue-filters';
const DEFAULT_PAGE_LIMIT = 25;
/**
 * The package-id search box is the FilterBar's own search input, and the kit
 * names that input by its placeholder, so the '/' shortcut finds it by this
 * string inside the filter toolbar.
 */
export const PACKAGE_ID_SEARCH_PLACEHOLDER = 'Search by package ID...';

// ---------------------------------------------------------------------------
// Filter definitions
// ---------------------------------------------------------------------------

// Status options come from the one label map keyed by the generated
// PledgeStatus union, which the listTasks status parameter takes whole
// (platform-admin.openapi.yaml /admin/tasks). The hand list here omitted
// AWAITING_SIGNATURE. With no status filter the queue shows PENDING and
// ACKNOWLEDGED only, the work the FI owes (task.service.ts
// TASK_VISIBLE_STATUSES); choosing another status, AWAITING_SIGNATURE
// included, is a lookup the route answers.
//
// The package id is not a filter here: it is the FilterBar search, so one
// control edits it (r3-op-g1-13).
export const FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: [...PLEDGE_STATUS_FILTER_OPTIONS],
  },
  {
    id: 'ageBand',
    label: 'Age Band',
    type: 'select',
    options: [
      { value: 'green', label: 'Green (within SLA)' },
      { value: 'yellow', label: 'Yellow (approaching SLA)' },
      { value: 'red', label: 'Red (over SLA)' },
    ],
  },
  {
    id: 'assignedReviewer',
    label: 'Assigned Reviewer',
    type: 'text',
    placeholder: 'Reviewer id or email...',
  },
];

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined) return '--';
  if (value < 1) return `${Math.round(value * 60)}m`;
  if (value < 24) return `${value.toFixed(1)}h`;
  const days = value / 24;
  return `${days.toFixed(1)}d`;
}

// ---------------------------------------------------------------------------
// Age-band badge -- maps to Badge variants (green->success, yellow->warning,
// red->error). Kept as a thin wrapper around the package primitive so no
// styling duplication is introduced.
// ---------------------------------------------------------------------------

function AgeBandBadge({ ageBand }: { ageBand: TaskAgeBand }) {
  const variant: 'success' | 'warning' | 'error' =
    ageBand === 'green' ? 'success' : ageBand === 'yellow' ? 'warning' : 'error';
  const label =
    ageBand === 'green'
      ? 'Within SLA'
      : ageBand === 'yellow'
        ? 'Approaching'
        : 'Over SLA';
  return (
    <Badge variant={variant} size="small">
      {label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Task-count tiles
// ---------------------------------------------------------------------------

/** The MetricCard props a task-count tile fills. */
type CountTileProps = { value: string; caption?: string; description?: string };

/**
 * The Pending Tasks and Over SLA tiles. "None pending" and "Within SLA" are
 * statements about a count the server returned, so they print only from a
 * returned count. Before the count arrives, or when its read fails, the tile
 * prints "--" with the reason; it used to print 0 with "None pending" and
 * "Within SLA" in the positive color.
 */
export function taskCountTiles(input: {
  pendingCount: number | undefined;
  overSlaCount: number | undefined;
  isError: boolean;
}): { pending: CountTileProps; overSla: CountTileProps } {
  const withheld = input.isError
    ? METRIC_READ_CAPTIONS.failed
    : METRIC_READ_CAPTIONS.unread;
  const pending: CountTileProps =
    input.pendingCount === undefined || input.isError
      ? { value: '--', caption: withheld }
      : {
          value: formatCount(input.pendingCount),
          description: input.pendingCount > 0 ? 'Awaiting FI action' : 'None pending',
        };
  const overSla: CountTileProps =
    input.overSlaCount === undefined || input.isError
      ? { value: '--', caption: withheld }
      : {
          value: formatCount(input.overSlaCount),
          description: input.overSlaCount > 0 ? 'Requires attention' : 'Within SLA',
        };
  return { pending, overSla };
}

/** The grid's empty message, naming what is absent and under which filters. */
export function taskQueueEmptyMessage(filtered: boolean): string {
  return filtered
    ? 'No pledges match these filters.'
    : 'No pledges are waiting on FI action across any tenant.';
}

// ---------------------------------------------------------------------------
// Saved-filter persistence -- MVP: localStorage only; no cross-device sync.
// Key documented in mandate: `aaim-171-task-queue-filters`.
// ---------------------------------------------------------------------------

function readSavedViews(): SavedView[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(SAVED_VIEWS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SavedView[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeSavedViews(views: SavedView[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SAVED_VIEWS_STORAGE_KEY, JSON.stringify(views));
  } catch {
    // Storage quota or disabled -- silent fail. Views are non-essential.
  }
}

// ---------------------------------------------------------------------------
// TasksPage
// ---------------------------------------------------------------------------

export function TasksPage() {

  // ---- Filter state --------------------------------------------------------

  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  // The FilterBar search box. It narrows the list by package-id prefix and
  // is kept out of activeFilters, so no chip repeats what the box shows.
  const [packageIdSearch, setPackageIdSearch] = useState<string>('');
  const packageIdFilter = packageIdSearch.trim() || undefined;
  const [savedViews, setSavedViews] = useState<SavedView[]>(() =>
    readSavedViews(),
  );

  useEffect(() => {
    writeSavedViews(savedViews);
  }, [savedViews]);

  const statusFilter = activeFilters.find((f) => f.filterId === 'status')
    ?.value as PledgeStatus | undefined;
  const ageBandFilter = activeFilters.find((f) => f.filterId === 'ageBand')
    ?.value as TaskAgeBand | undefined;
  const assignedReviewerFilter = activeFilters.find(
    (f) => f.filterId === 'assignedReviewer',
  )?.value;

  // ---- Cursor pagination state -- accumulate pages client-side ------------

  // historicalPages holds items fetched from prior pagination steps. The
  // currently-visible page is appended from listData.items at render time so
  // we do not need a useEffect that depends on the query result reference
  // (which can change identity on every render under mocked conditions and
  // cause an infinite render loop).
  const [historicalPages, setHistoricalPages] = useState<Task[][]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const lastAppendedCursorRef = useRef<string | undefined>(undefined);

  // Reset accumulated pages when filters change.
  const filterSignature = useMemo(
    () =>
      JSON.stringify({
        statusFilter,
        ageBandFilter,
        assignedReviewerFilter,
        packageIdFilter,
      }),
    [statusFilter, ageBandFilter, assignedReviewerFilter, packageIdFilter],
  );

  useEffect(() => {
    setHistoricalPages([]);
    setCursor(undefined);
    lastAppendedCursorRef.current = undefined;
  }, [filterSignature]);

  const listParams: TaskListQueryOptions = useMemo(
    () => ({
      status: statusFilter,
      ageBand: ageBandFilter,
      assignedReviewer: assignedReviewerFilter,
      packageId: packageIdFilter,
      cursor,
      limit: DEFAULT_PAGE_LIMIT,
    }),
    [
      statusFilter,
      ageBandFilter,
      assignedReviewerFilter,
      packageIdFilter,
      cursor,
    ],
  );

  const {
    data: listData,
    isLoading: isListLoading,
    error: listError,
  } = useTasks(listParams);
  const { data: countData, isError: isCountError } = useTaskCount();

  const currentItems: Task[] = listData?.items ?? [];
  const tasks: Task[] = useMemo(
    () => [...historicalPages.flat(), ...currentItems],
    [historicalPages, currentItems],
  );
  const nextCursor = listData?.nextCursor ?? null;

  // ---- Selection state -----------------------------------------------------

  const [selectedTasks, setSelectedTasks] = useState<Task[]>([]);
  // Clear selection on filter change.
  useEffect(() => {
    setSelectedTasks([]);
  }, [filterSignature]);

  // ---- Mutations -----------------------------------------------------------

  const ackMutation = useBulkAcknowledgeTasks();
  const returnMutation = useBulkReturnTasks();
  const reassignMutation = useReassignTask();

  // ---- Dialog state --------------------------------------------------------

  const [returnOpen, setReturnOpen] = useState(false);
  const [returnReason, setReturnReason] = useState('');

  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState<Task | null>(null);
  const [reassignReviewer, setReassignReviewer] = useState('');
  const [reassignReason, setReassignReason] = useState('');

  const [actionError, setActionError] = useState<string | null>(null);

  // ---- Keyboard-shortcut state --------------------------------------------
  //
  // focusedRowIndex is 0-based into the currently-rendered `tasks` array. It
  // tracks the row receiving the j/k navigation focus (distinct from the
  // checkbox selection state tracked in `selectedTasks`). -1 means no row is
  // focused -- j/k on an empty state will stay at -1 until data loads.
  const [focusedRowIndex, setFocusedRowIndex] = useState<number>(-1);
  const [helpOpen, setHelpOpen] = useState<boolean>(false);
  const pageContainerRef = useRef<HTMLDivElement | null>(null);

  // Keep the focused-row index inside the bounds of the current list. When
  // the list shrinks (filter change, post-action refetch) clamp the cursor.
  useEffect(() => {
    if (tasks.length === 0) {
      setFocusedRowIndex(-1);
      return;
    }
    setFocusedRowIndex((prev) => {
      if (prev < 0) return 0;
      if (prev >= tasks.length) return tasks.length - 1;
      return prev;
    });
  }, [tasks.length]);

  // ---- Handlers ------------------------------------------------------------

  const handleLoadMore = useCallback(() => {
    if (!nextCursor) return;
    // Capture the items of the currently-visible page BEFORE advancing the
    // cursor. Using a ref guard prevents double-appending if the user clicks
    // twice before the next page has resolved.
    if (lastAppendedCursorRef.current !== cursor) {
      const snapshot = listData?.items ?? [];
      setHistoricalPages((prev) => [...prev, snapshot]);
      lastAppendedCursorRef.current = cursor;
    }
    setCursor(nextCursor);
  }, [nextCursor, cursor, listData]);

  const handleBulkAcknowledge = useCallback(async () => {
    setActionError(null);
    const pledgeIds = selectedTasks.map((t) => t.pledgeId);
    if (pledgeIds.length === 0) return;
    try {
      const result = await ackMutation.mutateAsync({ pledgeIds });
      if (result.failed.length > 0) {
        setActionError(
          `Acknowledged ${result.succeeded.length} of ${pledgeIds.length}. ` +
            `${result.failed.length} failed: ${result.failed[0]?.reason ?? 'unknown reason'}`,
        );
      }
      setSelectedTasks([]);
      setHistoricalPages([]); lastAppendedCursorRef.current = undefined;
      setCursor(undefined);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Bulk acknowledge failed',
      );
    }
  }, [ackMutation, selectedTasks]);

  const handleOpenReturnDialog = useCallback(() => {
    setReturnReason('');
    setActionError(null);
    setReturnOpen(true);
  }, []);

  const handleBulkReturn = useCallback(async () => {
    setActionError(null);
    const pledgeIds = selectedTasks.map((t) => t.pledgeId);
    if (pledgeIds.length === 0 || !returnReason.trim()) return;
    try {
      const result = await returnMutation.mutateAsync({
        pledgeIds,
        reason: returnReason.trim(),
      });
      if (result.failed.length > 0) {
        setActionError(
          `Returned ${result.succeeded.length} of ${pledgeIds.length}. ` +
            `${result.failed.length} failed: ${result.failed[0]?.reason ?? 'unknown reason'}`,
        );
      }
      setReturnOpen(false);
      setReturnReason('');
      setSelectedTasks([]);
      setHistoricalPages([]); lastAppendedCursorRef.current = undefined;
      setCursor(undefined);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Bulk return failed',
      );
    }
  }, [returnMutation, selectedTasks, returnReason]);

  const handleOpenReassignDialog = useCallback((task: Task) => {
    setReassignTarget(task);
    setReassignReviewer(task.assignedReviewer ?? '');
    setReassignReason('');
    setActionError(null);
    setReassignOpen(true);
  }, []);

  const handleReassign = useCallback(async () => {
    if (!reassignTarget || !reassignReviewer.trim()) return;
    setActionError(null);
    try {
      await reassignMutation.mutateAsync({
        pledgeId: reassignTarget.pledgeId,
        body: {
          newReviewerId: reassignReviewer.trim(),
          reason: reassignReason.trim() ? reassignReason.trim() : null,
        },
      });
      setReassignOpen(false);
      setReassignTarget(null);
      setReassignReviewer('');
      setReassignReason('');
      setHistoricalPages([]); lastAppendedCursorRef.current = undefined;
      setCursor(undefined);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Reassign failed',
      );
    }
  }, [reassignMutation, reassignTarget, reassignReviewer, reassignReason]);

  // ---- SavedView callbacks -------------------------------------------------

  const handleSaveView = useCallback(
    (name: string, filters: ActiveFilter[]) => {
      const newView: SavedView = {
        id: `view-${Date.now()}`,
        name,
        filters,
      };
      setSavedViews((prev) => [...prev, newView]);
    },
    [],
  );

  // A view saved before the package id moved into the search box carries it
  // as a 'packageId' filter. It loads into the box, not as a chip for a
  // filter the bar no longer defines.
  const handleLoadView = useCallback((view: SavedView) => {
    const savedPackageId = view.filters.find((f) => f.filterId === 'packageId');
    setActiveFilters(view.filters.filter((f) => f.filterId !== 'packageId'));
    setPackageIdSearch(savedPackageId?.value ?? '');
  }, []);

  const handleDeleteView = useCallback((viewId: string) => {
    setSavedViews((prev) => prev.filter((v) => v.id !== viewId));
  }, []);

  // ---- Keyboard-shortcut handlers -----------------------------------------

  const toggleRowSelection = useCallback(
    (task: Task) => {
      setSelectedTasks((prev) => {
        const exists = prev.some((t) => t.pledgeId === task.pledgeId);
        if (exists) return prev.filter((t) => t.pledgeId !== task.pledgeId);
        return [...prev, task];
      });
    },
    [],
  );

  const focusPackageIdSearch = useCallback(() => {
    const input = pageContainerRef.current?.querySelector(
      `[role="toolbar"] input[aria-label="${PACKAGE_ID_SEARCH_PLACEHOLDER}"]`,
    );
    if (input instanceof HTMLInputElement) {
      input.focus();
      input.select();
    }
  }, []);

  const focusNextFilterControl = useCallback(() => {
    const container = pageContainerRef.current;
    if (!container) return;
    const toolbar = container.querySelector('[role="toolbar"]');
    if (!(toolbar instanceof HTMLElement)) return;
    const focusables = Array.from(
      toolbar.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );
    if (focusables.length === 0) return;
    const active = document.activeElement;
    const currentIndex = focusables.findIndex((el) => el === active);
    const next = focusables[(currentIndex + 1) % focusables.length];
    next?.focus();
  }, []);

  const closeAnyOpenOverlay = useCallback(() => {
    if (helpOpen) {
      setHelpOpen(false);
      return;
    }
    if (returnOpen) {
      setReturnOpen(false);
      return;
    }
    if (reassignOpen) {
      setReassignOpen(false);
    }
  }, [helpOpen, returnOpen, reassignOpen]);

  // ---- Shortcut map (must come AFTER handlers it references) --------------

  const shortcuts = useMemo<KeyboardShortcutConfig[]>(() => {
    const focusedTask: Task | undefined =
      focusedRowIndex >= 0 ? tasks[focusedRowIndex] : undefined;
    return [
      {
        keys: 'j',
        description: 'Move focus down one row',
        handler: () => {
          if (tasks.length === 0) return;
          setFocusedRowIndex((prev) =>
            prev < 0 ? 0 : Math.min(prev + 1, tasks.length - 1),
          );
        },
      },
      {
        keys: 'k',
        description: 'Move focus up one row',
        handler: () => {
          if (tasks.length === 0) return;
          setFocusedRowIndex((prev) => (prev <= 0 ? 0 : prev - 1));
        },
      },
      {
        keys: 'x',
        description: 'Toggle selection on the focused row',
        handler: () => {
          if (focusedTask) toggleRowSelection(focusedTask);
        },
      },
      {
        keys: ' ',
        description: 'Toggle selection on the focused row (space)',
        handler: () => {
          if (focusedTask) toggleRowSelection(focusedTask);
        },
      },
      {
        keys: 'a',
        description: 'Acknowledge the currently selected tasks',
        handler: () => {
          if (selectedTasks.length === 0) return;
          void handleBulkAcknowledge();
        },
      },
      {
        keys: 'r',
        description: 'Return the currently selected tasks (opens dialog)',
        handler: () => {
          if (selectedTasks.length === 0) return;
          handleOpenReturnDialog();
        },
      },
      {
        keys: 'e',
        description: 'Reassign the focused row (opens dialog)',
        handler: () => {
          if (focusedTask) handleOpenReassignDialog(focusedTask);
        },
      },
      {
        keys: '?',
        description: 'Open this keyboard shortcut help',
        handler: () => setHelpOpen(true),
      },
      {
        keys: 'Escape',
        description: 'Close any open dialog or help overlay',
        allowWhenModalOpen: true,
        handler: closeAnyOpenOverlay,
      },
      {
        keys: '/',
        description: 'Focus the package ID search',
        handler: focusPackageIdSearch,
      },
      {
        keys: 'g f',
        description: 'Cycle focus through filter controls',
        handler: focusNextFilterControl,
      },
    ];
  }, [
    tasks,
    focusedRowIndex,
    selectedTasks,
    toggleRowSelection,
    handleBulkAcknowledge,
    handleOpenReturnDialog,
    handleOpenReassignDialog,
    closeAnyOpenOverlay,
    focusPackageIdSearch,
    focusNextFilterControl,
  ]);

  useKeyboardShortcuts(shortcuts, { containerRef: pageContainerRef });

  // ---- Columns -------------------------------------------------------------

  const columns = useMemo<ColumnDef<Task, unknown>[]>(
    () => [
      {
        accessorKey: 'packageId',
        header: 'Package ID',
        // The kit TableLink, a real link to the pledge in the secondary
        // color. The red <button> set the package id in the CTA color.
        cell: ({ row }) => (
          <RouterTableLink
            to={`/pledges/${row.original.pledgeId}`}
            className="text-sm font-mono tabular-nums leading-normal"
          >
            {row.original.packageId}
          </RouterTableLink>
        ),
        size: 220,
      },
      {
        accessorKey: 'consumerName',
        header: 'Member',
        cell: ({ row }) => (
          <span className="text-sm font-sans text-primary leading-normal">
            {row.original.consumerName ?? '--'}
          </span>
        ),
        size: 200,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => <PledgeStatusBadge status={getValue() as string} />,
        size: 130,
      },
      {
        accessorKey: 'ageBand',
        header: 'SLA',
        cell: ({ row }) => <AgeBandBadge ageBand={row.original.ageBand} />,
        size: 130,
      },
      {
        accessorKey: 'ageHours',
        header: 'Age',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-secondary tabular-nums leading-normal">
            {formatHours(getValue() as number)}
          </span>
        ),
        size: 90,
      },
      {
        accessorKey: 'assignedReviewer',
        header: 'Assigned',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {(getValue() as string | null | undefined) ?? 'Unassigned'}
          </span>
        ),
        size: 180,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {formatDateTime(getValue() as string)}
          </span>
        ),
        size: 170,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button
            variant="secondary"
            size="sm"
            icon={UserPlusIcon}
            onClick={() => handleOpenReassignDialog(row.original)}
          >
            Reassign
          </Button>
        ),
        size: 130,
      },
    ],
    [handleOpenReassignDialog],
  );

  // ---- Bulk-action toolbar -------------------------------------------------

  const bulkActions: ActionDefinition[] = useMemo(
    () => [
      {
        id: 'acknowledge',
        label: 'Acknowledge',
        icon: CheckIcon,
        variant: 'primary',
        onClick: handleBulkAcknowledge,
        disabled: ackMutation.isPending || selectedTasks.length === 0,
      },
      {
        id: 'return',
        label: 'Return',
        icon: CornerUpLeftIcon,
        variant: 'secondary',
        onClick: handleOpenReturnDialog,
        disabled: returnMutation.isPending || selectedTasks.length === 0,
      },
    ],
    [
      handleBulkAcknowledge,
      handleOpenReturnDialog,
      ackMutation.isPending,
      returnMutation.isPending,
      selectedTasks.length,
    ],
  );

  // ---- Derived metrics -----------------------------------------------------

  const countTiles = taskCountTiles({
    pendingCount: countData?.pendingCount,
    overSlaCount: countData?.overSlaCount,
    isError: isCountError,
  });
  const isFiltered = activeFilters.length > 0 || packageIdSearch.trim() !== '';

  // ---- Render --------------------------------------------------------------

  // Focus-ring for the j/k-focused row. Emitted as a scoped <style> tag so
  // we do NOT override DataGrid internals; the selector targets the row's
  // aria-rowindex (index + 2 in tanstack-table's convention) inside our
  // wrapper.
  const focusedAriaRowIndex =
    focusedRowIndex >= 0 ? focusedRowIndex + 2 : null;

  return (
    <div ref={pageContainerRef} tabIndex={-1} data-testid="task-queue-page">
      {focusedAriaRowIndex !== null && (
        <style>
          {/* The keyboard focus marker uses the selection token (growth),
              never the red accent, which is reserved for CTAs and alerts.
              --color-* variables hold bare RGB triples, so they are wrapped
              in rgb(); the former var(--color-accent, #D2001F) resolved to
              "inset 2px 0 0 210 0 31", an invalid box-shadow the browser
              dropped, so the marker never drew. */}
          {`[data-testid="task-queue-page"] [role="row"][aria-rowindex="${focusedAriaRowIndex}"] > td { background-color: rgb(var(--color-growth) / 0.06); } [data-testid="task-queue-page"] [role="row"][aria-rowindex="${focusedAriaRowIndex}"] > td:first-child { box-shadow: inset 2px 0 0 rgb(var(--color-growth)); }`}
        </style>
      )}

      {/* The package-id search lives in the FilterBar below, as on Tenants
          and Pledges. A raw box sat here alone above the tiles and wrote into
          a second 'Package ID' filter, so two controls edited one value. */}
      <div className="flex justify-end mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setHelpOpen(true)}
          aria-label="Show keyboard shortcuts"
        >
          Shortcuts (?)
        </Button>
      </div>

      {listError && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            Failed to load tasks: {listError.message}
          </div>
        </div>
      )}

      {actionError && (
        <div className="bg-warning/5 border border-warning/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-warning flex-shrink-0" />
          <div className="text-sm font-sans text-warning">{actionError}</div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-6">
        <MetricCard
          label="Pending Tasks"
          value={countTiles.pending.value}
          caption={countTiles.pending.caption}
          description={countTiles.pending.description}
        />
        <MetricCard
          label="Over SLA"
          value={countTiles.overSla.value}
          caption={countTiles.overSla.caption}
          description={countTiles.overSla.description}
        />
      </div>

      <FilterBar
        filters={FILTER_DEFINITIONS}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={packageIdSearch}
        onSearchChange={setPackageIdSearch}
        searchPlaceholder={PACKAGE_ID_SEARCH_PLACEHOLDER}
        savedViews={savedViews}
        onSaveView={handleSaveView}
        onLoadView={handleLoadView}
        onDeleteView={handleDeleteView}
        className="rounded-t-sm"
      />

      <ActionBar
        selectedCount={selectedTasks.length}
        totalCount={tasks.length}
        actions={bulkActions}
        onSelectAll={() => setSelectedTasks(tasks)}
        onDeselectAll={() => setSelectedTasks([])}
      />

      {isListLoading && tasks.length === 0 ? (
        <div className="bg-surface rounded-sm shadow-sm p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full mb-2" />
          ))}
        </div>
      ) : (
        <DataGrid<Task>
          data={tasks}
          columns={columns}
          enableRowSelection
          enableMultiRowSelection
          onSelectionChange={(rows: Task[]) => setSelectedTasks(rows)}
          enableColumnResize
          hoverable
          striped
          getRowId={(row) => row.pledgeId}
          emptyMessage={taskQueueEmptyMessage(isFiltered)}
          className="w-full rounded-b-sm shadow-sm"
        />
      )}

      {nextCursor && (
        <div className="flex flex-col items-center gap-2 mt-4">
          <p className="text-sm font-sans text-tertiary">
            Showing {formatCount(tasks.length)} tasks. More are available.
          </p>
          <Button
            variant="secondary"
            size="medium"
            onClick={handleLoadMore}
            disabled={isListLoading}
          >
            {isListLoading ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Return Dialog                                                      */}
      {/* ----------------------------------------------------------------- */}
      <Dialog
        open={returnOpen}
        onOpenChange={setReturnOpen}
        title={`Return ${selectedTasks.length} ${selectedTasks.length === 1 ? 'task' : 'tasks'}`}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              size="medium"
              onClick={() => setReturnOpen(false)}
              disabled={returnMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={handleBulkReturn}
              disabled={!returnReason.trim() || returnMutation.isPending}
            >
              {returnMutation.isPending ? 'Returning...' : 'Confirm return'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm font-sans text-secondary">
            The reason is recorded in the audit log for each pledge and surfaced
            to the consumer as the explanation for the returned pledge.
          </p>
          <Textarea
            label="Return reason"
            placeholder="Explain what needs revision..."
            value={returnReason}
            onChange={setReturnReason}
            required
            rows={4}
            maxLength={500}
            showCharCount
          />
        </div>
      </Dialog>

      {/* ----------------------------------------------------------------- */}
      {/* Reassign Dialog                                                    */}
      {/* ----------------------------------------------------------------- */}
      <Dialog
        open={reassignOpen}
        onOpenChange={setReassignOpen}
        title={
          reassignTarget
            ? `Reassign ${reassignTarget.packageId}`
            : 'Reassign task'
        }
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              size="medium"
              onClick={() => setReassignOpen(false)}
              disabled={reassignMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={handleReassign}
              disabled={
                !reassignReviewer.trim() || reassignMutation.isPending
              }
            >
              {reassignMutation.isPending ? 'Reassigning...' : 'Confirm reassign'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="New reviewer"
            placeholder="Reviewer user id"
            value={reassignReviewer}
            onChange={(e) => setReassignReviewer(e.target.value)}
            required
          />
          <Input
            label="Reason (optional)"
            placeholder="Why this reviewer is being reassigned..."
            value={reassignReason}
            onChange={(e) => setReassignReason(e.target.value)}
          />
        </div>
      </Dialog>

      {/* ----------------------------------------------------------------- */}
      {/* Keyboard Shortcut Help                                             */}
      {/* ----------------------------------------------------------------- */}
      <ShortcutHelpOverlay
        open={helpOpen}
        onOpenChange={setHelpOpen}
        shortcuts={shortcuts}
      />
    </div>
  );
}
